import { createContext, useState, useEffect } from 'react';
import Raven from 'raven-js';
import { fetchEngagementAssociationsSummary, getAssociatedObjectIds, hasAssociationToObject, baseAssociationsObject } from '../apis/EngagementAssociationsApi';
import { FAILED, PENDING, SUCCEEDED, UNINITIALIZED } from '../types/RequestStatus';
import { normalizeTypeId } from 'customer-data-objects/constants/ObjectTypeIds';
import { isEngagementTypeId } from 'customer-data-objects/engagement/isEngagementTypeId';
import { useOnCrmMessageTopic } from 'crm-message-bus/useOnCrmMessageTopic';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import { formatAssociationsForProsemirror } from '../utils/associationsUtils';
import { jsx as _jsx } from "react/jsx-runtime";
export const EngagementAssociationsContext = /*#__PURE__*/createContext({});
export const EngagementAssociationsContextProvider = props => {
  const {
    children,
    objectId,
    objectTypeId,
    subjectObjectId,
    skip = false,
    initialAssociations
  } = props;
  const [associationsSummary, setAssociationsSummary] = useState(initialAssociations !== null && initialAssociations !== void 0 ? initialAssociations : null);
  const [fetchStatus, setFetchStatus] = useState(initialAssociations ? SUCCEEDED : UNINITIALIZED);
  const handleRefetchAssociations = ({
    objectId: refetchObjectId,
    objectTypeId: refetchObjectTypeId
  }) => {
    if (!isEngagementTypeId(refetchObjectTypeId)) {
      if (refetchObjectTypeId !== normalizeTypeId(objectTypeId)) {
        return;
      }
    }
    if (refetchObjectId === String(objectId)) {
      setFetchStatus(UNINITIALIZED);
    }
  };
  useOnCrmMessageTopic(TOPIC_NAMES.COMMENTS_UPDATED, handleRefetchAssociations, {
    ignoreLocal: true
  });
  useEffect(() => {
    if (skip || fetchStatus !== UNINITIALIZED || initialAssociations) {
      return;
    }
    setFetchStatus(PENDING);
    fetchEngagementAssociationsSummary(objectId, objectTypeId).then(results => {
      setAssociationsSummary(results);
      setFetchStatus(SUCCEEDED);
    }).catch(error => {
      if (error.networkError && error.networkError.status === 401) {
        // 401s are expected when the user does not have permissions to view the engagement, but it
        // is still returned by Minkowski
        setFetchStatus(SUCCEEDED);
        return;
      }
      error.message = `[EngagementAssociationsContext|fetchEngagementAssociationsSummary]${error.message}`;
      setFetchStatus(FAILED);
      Raven.captureException(error, {
        tags: {
          apiError: 'Error fetching engagement associations summary'
        }
      });
    });
  }, [objectId, objectTypeId, fetchStatus, skip, initialAssociations]);
  const handleGetAssociatedObjectIds = targetObjectTypeId => {
    return getAssociatedObjectIds({
      objectTypeId: targetObjectTypeId,
      summaryAssociations: associationsSummary === null || associationsSummary === void 0 ? void 0 : associationsSummary.objectTypeIdsToAssociatedObjectIds
    });
  };
  const handleHasAssociationToObject = (targetObjectId, targetObjectTypeId) => {
    return hasAssociationToObject({
      objectId: targetObjectId,
      objectTypeId: targetObjectTypeId,
      summaryAssociations: associationsSummary === null || associationsSummary === void 0 ? void 0 : associationsSummary.objectTypeIdsToAssociatedObjectIds
    });
  };
  const contextValue = {
    associationsSummary,
    formattedAssociations: formatAssociationsForProsemirror({
      associationsSummary,
      objectId: subjectObjectId,
      baseAssociationsObject
    }),
    fetchStatus,
    getAssociatedObjectIds: handleGetAssociatedObjectIds,
    hasAssociationToObject: handleHasAssociationToObject
  };
  return /*#__PURE__*/_jsx(EngagementAssociationsContext.Provider, {
    value: contextValue,
    children: children
  });
};